import { useState } from "react";
import { useImmer } from "use-immer";
import InfiniteScroll from "react-infinite-scroll-component";
import { MealSourceDto, MealSourceIdDto } from "../../api";
import {
  computeResultType,
  getMealSourceKey,
} from "../../utils/mealSourceUtils";
import { JustIntake } from "../../data/plan";
import { searchMealSource } from "../../api";
import { SearchInput } from "../SearchInput";
import { DetailSearchResult, isRecent } from "../DetailSearchResult";
import { usePerformSearch } from "../../utils/searchUtils";
import { useNutriSlider } from "../nutriSlider";
import { usePurchase } from "../../queries/caches";
import { UnitAmount } from "../UnitAmount";
import { FoodUnit } from "../../data/foodUnit";
import { formatPurchaseDescription } from "../../utils/purchaseUtils";
import { SmallIconButton } from "../SmallIconButton";
import { nutriValuesFromIntake } from "../../utils/planUtils";
import { NutriRow } from "../ui/NutriRow";

function AdditionalIntakeRow(props: {
  intake: JustIntake;
  onRemove: () => void;
  onUpdate: (patch: (i: JustIntake) => void) => void;
}) {
  const { intake } = props;
  const purchase = usePurchase(intake.purchaseUnitPurchaseId);
  const purchaseUnit = purchase?.purchaseUnits.find(
    (pu) => pu.id === intake.purchaseUnitId,
  );
  const name = purchase
    ? formatPurchaseDescription(purchase)
    : intake.purchaseUnitPurchaseDescription;
  const nutri = purchase
    ? nutriValuesFromIntake(intake, [purchase])
    : undefined;

  return (
    <div
      data-cy="verzehr-additional-row"
      className="flex flex-col gap-2 rounded-card border border-line bg-surface-card px-3 py-2.5 shadow-card"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 flex-1 truncate text-sm text-ink">{name}</span>
        <SmallIconButton
          label="Entfernen"
          icon="delete"
          variant="destructive"
          onClick={props.onRemove}
        />
      </div>
      <UnitAmount
        foodUnit={purchaseUnit as FoodUnit | undefined}
        units={(purchase?.purchaseUnits || []) as FoodUnit[]}
        amount={intake.amount}
        onUnitChange={(u) =>
          props.onUpdate((i) => {
            i.purchaseUnitId = u?.id ?? -1;
          })
        }
        onAmountChange={(a) =>
          props.onUpdate((i) => {
            i.amount = a;
          })
        }
      />
      {nutri && <NutriRow nutri={nutri} />}
    </div>
  );
}

export function AdditionalIntakeSection(props: {
  mealIndDescription: string;
  justIntakes: JustIntake[];
  onAddMealSource: (mealSource: MealSourceIdDto) => Promise<void>;
  onRemove: (index: number) => void;
  onUpdate: (index: number, patch: (i: JustIntake) => void) => void;
}) {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, updateSearchResults] = useImmer<MealSourceDto[]>([]);
  const [searchResultCount, setSearchResultCount] = useState(0);
  const nutriSliderProps = useNutriSlider("dish");

  const { performSearch, fetchMoreData } = usePerformSearch(
    undefined,
    nutriSliderProps,
    searchTerm,
    searchResults,
    updateSearchResults,
    setSearchResultCount,
    searchMealSource,
    10,
  );

  return (
    <div className="flex w-full flex-col gap-2">
      <p className="px-1 text-xs font-semibold uppercase tracking-wider text-ink-muted">
        Zusätzlich gegessen
      </p>
      <SearchInput
        dataCy="verzehr-additional-search"
        value={searchTerm}
        setSearchTerm={setSearchTerm}
        performSearch={performSearch}
        placeholder={props.mealIndDescription + " suchen"}
      />

      {props.justIntakes.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {props.justIntakes.map((intake, idx) => (
            <AdditionalIntakeRow
              key={idx}
              intake={intake}
              onRemove={() => props.onRemove(idx)}
              onUpdate={(patch) => props.onUpdate(idx, patch)}
            />
          ))}
        </div>
      )}

      {searchResults.length > 0 && (
        <>
          <div className="mt-1 h-2 w-full border-t border-solid border-line" />
          <InfiniteScroll
            scrollableTarget="intake-drawer-scroll"
            dataLength={searchResults.length}
            next={fetchMoreData}
            hasMore={searchResults.length < searchResultCount}
            loader={<h4>Loading…</h4>}
            style={{ width: "100%" }}
          >
            <div className="flex w-full flex-col gap-2">
              {searchResults.map((mealSource) => (
                <DetailSearchResult
                  key={getMealSourceKey(mealSource)}
                  extraTags={mealSource.extraTags}
                  tags={mealSource.tags}
                  title={mealSource.description}
                  resultType={computeResultType(mealSource)}
                  recent={isRecent(mealSource)}
                  purchaseRelevant={mealSource.purchaseRelevant}
                  nutriValues={mealSource}
                  showNumbers={
                    mealSource.indicator == "DISH" ||
                    mealSource.indicator == "MEAL"
                  }
                  onClick={async () => {
                    if (mealSource.indicator == "MEALS_FOR_DAY") return;
                    await props.onAddMealSource(mealSource);
                  }}
                />
              ))}
            </div>
          </InfiniteScroll>
        </>
      )}
    </div>
  );
}
